import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { getStylists, createStylist, archiveStylist } from '../services/stylistService';
import { updateStaffStatus } from '../services/profileService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  TextField,
  Box,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import StylistProfileSettings from '../components/profile/StylistProfileSettings';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import type { Stylist } from '@app-types/models/stylist';

interface ListingAction {
  title?: string;
  message?: string;
}

interface StoredUser {
  _id: string;
  email?: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
      requirements?: string[];
    };
  };
}

const StylistPage: React.FC = () => {
  const { t } = useTranslation();
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [selectedStylist, setSelectedStylist] = useState<Stylist | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [requirements, setRequirements] = useState<string[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showCreateStylistFromBusinessAccountDialog, setShowCreateStylistFromBusinessAccountDialog] = useState<boolean>(false);
  const [showListingDialog, setShowListingDialog] = useState<boolean>(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState<boolean>(false);
  const [listingAction, setListingAction] = useState<ListingAction>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchStylists = useCallback(async (): Promise<void> => {
    try {
      const result = await getStylists();
      setStylists(Array.isArray(result) ? result : []);
    } catch {
      setError(t('stylistPage.errorLoadingStylists'));
    }
  }, [t]);

  useEffect(() => {
    fetchStylists();
  }, [fetchStylists]);

  const sendMagicLink = async (email: string): Promise<boolean> => {
    try {
      const businessId = (JSON.parse(localStorage.getItem('user')!) as StoredUser)._id;
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/stylist-signup-page?businessId=${businessId}&email=${email}`
        }
      });
      if (error) throw error;
      return true;
    } catch (error: unknown) {
      console.error('Error sending magic link:', error);
      throw error;
    }
  };

  const handleInvite = async (): Promise<void> => {
    try {
      setError('');
      const emailExists = stylists.some(stylist => stylist.email === inviteEmail);
      if (emailExists) {
        setError(t('stylistPage.emailExists'));
        return;
      }

      await sendMagicLink(inviteEmail);
      await createStylist({ email: inviteEmail });
      toast.success(t('stylistPage.invitationSent'));
      setInviteEmail('');
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to send invitation:', error);
      setError(t('stylistPage.invitationFailed'));
    }
  };

  const handleViewDetails = (stylist: Stylist): void => {
    setSelectedStylist(stylist);
    setShowModal(true);
  };

  const handleListing = (stylist: Stylist): void => {
    setSelectedStylist(stylist);
    setError('');
    setRequirements([]);
    const isListing = stylist.status === 'listing';
    setListingAction({
      title: isListing ? t('stylistPage.stopListingConfirmationTitle') : t('stylistPage.listingConfirmationTitle'),
      message: isListing ? t('stylistPage.stopListingConfirmationMessage') : t('stylistPage.listingConfirmationMessage'),
    });
    setShowListingDialog(true);
  };

  const handleConfirmListingChange = async (): Promise<void> => {
    try {
      const newStatus = selectedStylist!.status === 'listing' ? 'not_listing' : 'listing';
      await updateStaffStatus({ staffId: selectedStylist!._id, status: newStatus });
      toast.success(newStatus === 'listing' ? t('stylistPage.listingSuccess') : t('stylistPage.stopListingSuccess'));
      setError('');
      setRequirements([]);
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to update listing status:', error);
      const apiError = error as ApiError;

      setError(`${t('stylistPage.listingFailed')} ${apiError.response?.data?.message ?? ''}`);

      if (apiError.response?.data?.requirements) {
        setRequirements(apiError.response.data.requirements);
      }
    } finally {
      setShowListingDialog(false);
    }
  };

  const handleArchiveStylist = (stylist: Stylist): void => {
    setSelectedStylist(stylist);
    setShowArchiveDialog(true);
  };

  const handleConfirmArchiveStylist = async (): Promise<void> => {
    try {
      await archiveStylist(selectedStylist!._id);
      toast.success(t('stylistPage.archiveSuccess', 'Stylist archived successfully.'));
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to archive stylist:', error);
      setError(t('stylistPage.archiveFailed', 'Failed to archive stylist.'));
    } finally {
      setShowArchiveDialog(false);
      setSelectedStylist(null);
    }
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
    setSelectedStylist(null);
  };

  const handleCloseListingDialog = (): void => {
    setShowListingDialog(false);
    setSelectedStylist(null);
    setError('');
    setRequirements([]);
  };

  const handleCloseArchiveDialog = (): void => {
    setShowArchiveDialog(false);
    setSelectedStylist(null);
  };

  const handleCreateStylistFromBusinessAccount = (): void => {
    setShowCreateStylistFromBusinessAccountDialog(true);
    setError('');
    setRequirements([]);
  };

  const handleConfirmCreateStylistFromBusinessAccount = async (): Promise<void> => {
    try {
      const user: StoredUser = JSON.parse(localStorage.getItem('user')!);
      if (!user || !user.email) {
        setError(t('stylistPage.errorNoUserEmail', 'Could not find user email. Please log in again.'));
        return;
      }
      await createStylist({ email: user.email });
      toast.success(t('stylistPage.createSuccess'));
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to create stylist:', error);
      const apiError = error as ApiError;
      setError(`${t('stylistPage.createFailed')} ${apiError.response?.data?.message ?? ''}`);
    } finally {
      handleCloseCreateStylistFromBusinessAccountDialog();
    }
  };

  const handleCloseCreateStylistFromBusinessAccountDialog = (): void => {
    setShowCreateStylistFromBusinessAccountDialog(false);
  };

  return (
    <Container>
      <Box className="invite-section" sx={{mb: 2, display: 'flex', alignItems: 'center', gap: 2, maxWidth: 'calc(100vw - 32px)'}}>
        <TextField
          type="email"
          placeholder={t('stylistPage.invitePlaceholder')}
          value={inviteEmail}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteEmail(e.target.value)}
          sx={{ width: '300px' }}
        />
        <Button
          variant="contained"
          onClick={handleInvite}
        >
          {t('stylistPage.inviteButton')}
        </Button>
        <Button variant="contained" onClick={handleCreateStylistFromBusinessAccount}>
          {t('stylistPage.useSameEmail')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {requirements.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {requirements.map((req, index) => (
            <div key={index}>{req}</div>
          ))}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: 'calc(100vw - 32px)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('stylistPage.tableHeaders.name')}</TableCell>
              <TableCell>{t('stylistPage.tableHeaders.email')}</TableCell>
              <TableCell>{t('stylistPage.tableHeaders.role')}</TableCell>
              <TableCell>{t('stylistPage.tableHeaders.status')}</TableCell>
              <TableCell>{t('stylistPage.tableHeaders.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stylists.map((stylist) => (
              <TableRow key={stylist._id}>
                <TableCell>{stylist.displayName}</TableCell>
                <TableCell>{stylist.email}</TableCell>
                <TableCell>{stylist.role}</TableCell>
                <TableCell>{stylist.status}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={() => handleViewDetails(stylist)}>
                      {t('stylistPage.viewDetails')}
                    </Button>
                    <Button variant="contained" onClick={() => handleListing(stylist)}>
                      {stylist.status === 'listing' ? t('stylistPage.stopListing') : t('stylistPage.listing')}
                    </Button>
                    <Button variant="contained" color="warning" onClick={() => handleArchiveStylist(stylist)}>
                      {t('stylistPage.archive', 'Archive')}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t('stylistPage.editStylist')}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedStylist && <StylistProfileSettings stylist={selectedStylist} fetchStylists={fetchStylists} onClose={handleCloseModal}/>}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCreateStylistFromBusinessAccountDialog}
        onClose={handleCloseCreateStylistFromBusinessAccountDialog}
      >
        <DialogTitle>{t('stylistPage.useSameEmail')}</DialogTitle>
        <DialogContent>
          <p>{t('stylistPage.confirmCreateStylist')}</p>
          <Button onClick={handleCloseCreateStylistFromBusinessAccountDialog}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmCreateStylistFromBusinessAccount}>
            {t('common.confirm')}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showArchiveDialog}
        onClose={handleCloseArchiveDialog}
      >
        <DialogTitle>{t('stylistPage.confirmArchiveTitle', 'Confirm Archive')}</DialogTitle>
        <DialogContent>
          <p>{t('stylistPage.confirmArchiveMessage', 'Are you sure you want to archive this stylist? They will be hidden from view.')}</p>
          <Button onClick={handleCloseArchiveDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmArchiveStylist} color="warning">
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showListingDialog}
        onClose={handleCloseListingDialog}
      >
        <DialogTitle>{listingAction.title}</DialogTitle>
        <DialogContent>
          <p>{listingAction.message}</p>
          <Button onClick={handleCloseListingDialog}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmListingChange}>
            {t('common.confirm')}
          </Button>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default StylistPage;
